import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import bookingsService from '../../services/bookingsService';
import favoritesService from '../../services/favoritesService';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Chip,
  Stack,
  LinearProgress,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  BookOnline,
  AttachMoney,
  Favorite,
  Flight,
  ArrowForward,
  EditRoad,
  LocationOn,
  CalendarMonth,
  Bolt,
  RateReview,
  Explore,
  Person,
} from '@mui/icons-material';
import { getTripImageUrl } from '../../utils/tripImage';
import { formatCurrency } from '../../utils/currency';
import ButtonPill from '../ui/ButtonPill';

const UserDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = authService.getStoredUser();
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [bookingsRes, favoritesRes] = await Promise.all([
          bookingsService.getMyBookings().catch(() => ({ bookings: [] })),
          favoritesService.getFavorites().catch(() => ({ favorites: [] })),
        ]);
        setBookings(bookingsRes?.bookings || []);
        setFavorites(favoritesRes?.favorites || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dateOf = (b) => b.travel_date || b.travelDate;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const upcomingBookings = bookings
    .filter((b) => {
      const d = new Date(dateOf(b));
      d.setHours(0, 0, 0, 0);
      return d >= todayStart && (b.status || '').toLowerCase() !== 'cancelled';
    })
    .sort((a, b) => new Date(dateOf(a)) - new Date(dateOf(b)));

  const totalSpent = bookings
    .filter((b) => (b.payment_status || b.paymentStatus || '').toLowerCase() === 'paid')
    .reduce((sum, b) => sum + (parseFloat(b.total_price || b.totalPrice) || 0), 0);

  const firstName = user?.first_name || user?.firstName || (user?.email || 'traveler').split('@')[0];
  const nextTrip = upcomingBookings[0];
  const nextTripData = nextTrip?.trip || {};
  const nextTripImg = nextTrip ? getTripImageUrl(nextTripData) : null;
  const daysUntil = nextTrip ? Math.max(0, Math.ceil((new Date(dateOf(nextTrip)) - todayStart) / 86400000)) : null;
  const nextUnpaid = (nextTrip?.payment_status || nextTrip?.paymentStatus || '').toLowerCase() === 'unpaid';

  const stats = [
    { title: 'Total Bookings', value: bookings.length, icon: <BookOnline />, color: theme.palette.primary.main,
      sub: upcomingBookings.length > 0 ? `${upcomingBookings.length} upcoming` : 'Start exploring' },
    { title: 'Upcoming Trips', value: upcomingBookings.length, icon: <Flight />, color: '#1976D2',
      sub: nextTrip ? `Next in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}` : 'Browse trips' },
    { title: 'Total Spent', value: formatCurrency(totalSpent, nextTripData.currency), icon: <AttachMoney />, color: theme.palette.success.main,
      sub: bookings.length > 0 ? 'On confirmed trips' : 'Book your first trip' },
    { title: 'Saved Trips', value: favorites.length, icon: <Favorite />, color: '#C2185B',
      sub: favorites.length > 0 ? 'In your wishlist' : 'Save trips you love' },
  ];

  const profilePct = user?.phone && user?.city ? 100 : (user?.phone || user?.city ? 50 : 0);

  const recentActivity = bookings
    .slice(0, 4)
    .map((b, i) => ({
      id: b.id || i,
      title: `Booked ${b.trip?.title || 'a trip'}`,
      description: `${b.num_travelers || b.numTravelers || 1} traveler${(b.num_travelers || 1) !== 1 ? 's' : ''}`,
      timestamp: b.created_at || b.createdAt,
    }))
    .reverse();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sectionHeading = (text, action) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
        {text}
      </Typography>
      {action}
    </Box>
  );

  const cardSx = {
    p: { xs: 2.5, md: 3 },
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[200]}`,
    bgcolor: theme.palette.background.paper,
  };

  return (
    <Box>
      {/* ── Greeting ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: '"Playfair Display", serif' }}>
          Welcome back, {firstName} 👋
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          {nextTrip
            ? `Your next adventure is ${daysUntil === 0 ? 'today' : `just ${daysUntil} day${daysUntil !== 1 ? 's' : ''} away`}.`
            : "Let's plan something unforgettable."}
        </Typography>
      </Box>

      {/* ── Next Adventure hero ── */}
      {nextTrip ? (
        <Box
          onClick={() => navigate('/user/bookings')}
          sx={{
            position: 'relative',
            borderRadius: 5,
            overflow: 'hidden',
            minHeight: { xs: 300, md: 340 },
            mb: 4,
            cursor: 'pointer',
            boxShadow: '0 20px 48px rgba(0,0,0,0.18)',
            '&:hover .hero-img': { transform: 'scale(1.05)' },
          }}
        >
          <Box
            className="hero-img"
            component="img"
            src={nextTripImg || undefined}
            alt={nextTripData.title}
            sx={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', transition: 'transform 0.6s ease',
              bgcolor: theme.palette.primary.dark,
            }}
          />
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg, rgba(20,10,4,0.86) 0%, rgba(20,10,4,0.55) 45%, rgba(20,10,4,0.15) 100%)' }} />
          <Box sx={{ position: 'relative', p: { xs: 3, md: 5 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: { xs: 300, md: 340 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography sx={{ color: alpha('#fff', 0.85), fontWeight: 700, letterSpacing: 2, fontSize: '0.75rem' }}>
                YOUR NEXT ADVENTURE
              </Typography>
              <Chip
                label={daysUntil === 0 ? 'Today!' : `${daysUntil} day${daysUntil !== 1 ? 's' : ''} to go`}
                sx={{ bgcolor: theme.palette.primary.main, color: '#fff', fontWeight: 800, backdropFilter: 'blur(4px)' }}
              />
            </Box>
            <Box>
              <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, fontFamily: '"Playfair Display", serif', textShadow: '0 2px 12px rgba(0,0,0,0.4)', fontSize: { xs: '1.9rem', md: '2.7rem' }, mb: 1 }}>
                {nextTripData.title}
              </Typography>
              <Stack direction="row" spacing={2.5} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2.5 }}>
                <Typography sx={{ color: alpha('#fff', 0.92), display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn sx={{ fontSize: 18 }} /> {nextTripData.location || 'Destination'}
                </Typography>
                <Typography sx={{ color: alpha('#fff', 0.92), display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarMonth sx={{ fontSize: 18 }} />
                  {new Date(dateOf(nextTrip)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
                <Typography sx={{ color: '#fff', fontWeight: 800 }}>
                  {formatCurrency(nextTrip.total_price || nextTrip.totalPrice, nextTripData.currency)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <ButtonPill
                  variant="contained"
                  size="small"
                  endIcon={<ArrowForward />}
                  onClick={(e) => { e.stopPropagation(); navigate('/user/bookings'); }}
                  sx={{ bgcolor: '#fff', color: theme.palette.primary.main, '&:hover': { bgcolor: 'grey.100' } }}
                >
                  {nextUnpaid ? 'Complete payment' : 'View booking'}
                </ButtonPill>
                <Chip label={nextTrip.status} sx={{ bgcolor: alpha('#fff', 0.2), color: '#fff', fontWeight: 700, textTransform: 'capitalize' }} />
              </Stack>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: 5, mb: 4, p: { xs: 4, md: 6 }, textAlign: 'center', color: '#fff',
            background: `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: '0 20px 48px rgba(160,65,13,0.3)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: '"Playfair Display", serif', mb: 1 }}>
            Ready for your next adventure?
          </Typography>
          <Typography sx={{ opacity: 0.92, mb: 3, maxWidth: 480, mx: 'auto' }}>
            Explore our curated safaris and beach escapes, or build a custom trip from scratch.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <ButtonPill variant="contained" startIcon={<Explore />} onClick={() => navigate('/trips')}
              sx={{ bgcolor: '#fff', color: theme.palette.primary.main, '&:hover': { bgcolor: 'grey.100' } }}>
              Explore trips
            </ButtonPill>
            <ButtonPill variant="outlined" startIcon={<EditRoad />} onClick={() => navigate('/user/create-plan')}
              sx={{ borderColor: '#fff', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}>
              Build your own
            </ButtonPill>
          </Stack>
        </Box>
      )}

      {/* ── Stat tiles ── */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Paper
              elevation={0}
              sx={{
                ...cardSx, height: '100%', position: 'relative', overflow: 'hidden',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[8] },
              }}
            >
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 4, bgcolor: stat.color }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Avatar sx={{ bgcolor: alpha(stat.color, 0.12), color: stat.color, width: 44, height: 44 }}>
                  {stat.icon}
                </Avatar>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {stat.title}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stat.sub}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Body ── */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          {/* Favorites */}
          <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
            {sectionHeading('Saved for later',
              favorites.length > 0 && (
                <ButtonPill variant="text" size="small" endIcon={<ArrowForward />} onClick={() => navigate('/user/favorites')}>
                  View all
                </ButtonPill>
              ))}
            {favorites.length > 0 ? (
              <Grid container spacing={2}>
                {favorites.slice(0, 3).map((fav) => {
                  const trip = fav.trip || {};
                  return (
                    <Grid item xs={12} sm={4} key={fav.id}>
                      <Box
                        onClick={() => navigate(`/trips/${trip.id}`)}
                        sx={{
                          borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                          border: `1px solid ${theme.palette.grey[200]}`,
                          transition: 'all 0.25s ease',
                          '&:hover': { boxShadow: theme.shadows[6], transform: 'translateY(-3px)' },
                          '&:hover img': { transform: 'scale(1.06)' },
                        }}
                      >
                        <Box sx={{ height: 130, overflow: 'hidden' }}>
                          <Box component="img" src={getTripImageUrl(trip)} alt={trip.title}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }} />
                        </Box>
                        <Box sx={{ p: 1.75 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{trip.title}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }} noWrap>
                            <LocationOn sx={{ fontSize: 13 }} /> {trip.location}
                          </Typography>
                          <Typography sx={{ mt: 0.5, fontWeight: 800, color: theme.palette.primary.main }}>
                            {formatCurrency(trip.price, trip.currency)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Favorite sx={{ fontSize: 44, color: theme.palette.grey[300], mb: 1 }} />
                <Typography color="text.secondary" sx={{ mb: 2 }}>No saved trips yet.</Typography>
                <ButtonPill variant="outlined" size="small" onClick={() => navigate('/trips')}>Browse trips</ButtonPill>
              </Box>
            )}
          </Paper>

          {/* Build-your-own promo */}
          <Paper
            elevation={0}
            onClick={() => navigate('/user/create-plan')}
            sx={{
              p: { xs: 2.5, md: 3 }, borderRadius: 4, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap',
              background: `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha('#2E7D32', 0.08)})`,
              border: `1px solid ${theme.palette.grey[200]}`,
              transition: 'all 0.25s ease', '&:hover': { boxShadow: theme.shadows[6] },
            }}
          >
            <Avatar sx={{ bgcolor: alpha('#2E7D32', 0.15), color: '#2E7D32', width: 52, height: 52 }}>
              <EditRoad />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Dream up your own trip</Typography>
              <Typography variant="body2" color="text.secondary">
                Build a day-by-day custom itinerary and let our team make it real.
              </Typography>
            </Box>
            <ArrowForward sx={{ color: theme.palette.primary.main }} />
          </Paper>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} lg={4}>
          {/* Profile completion */}
          <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 44, height: 44, fontWeight: 700 }}>
                {(firstName[0] || 'U').toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{user?.full_name || firstName}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Profile completion</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{profilePct}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={profilePct}
              sx={{ height: 8, borderRadius: 5, mb: 2, bgcolor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: profilePct === 100 ? theme.palette.success.main : theme.palette.primary.main } }} />
            <ButtonPill variant="outlined" fullWidth size="small" startIcon={<Person />} onClick={() => navigate('/user/profile')}>
              {profilePct === 100 ? 'View profile' : 'Complete profile'}
            </ButtonPill>
          </Paper>

          {/* Quick actions */}
          <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Playfair Display", serif' }}>Quick actions</Typography>
            <Stack spacing={1.25}>
              {[
                { label: 'Explore trips', icon: <Explore />, to: '/trips' },
                { label: 'Build a custom trip', icon: <EditRoad />, to: '/user/create-plan' },
                { label: 'My requests', icon: <Bolt />, to: '/user/requests' },
                { label: 'Write a review', icon: <RateReview />, to: '/user/bookings' },
              ].map((a) => (
                <Box key={a.label} onClick={() => navigate(a.to)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, cursor: 'pointer',
                    transition: 'background 0.2s ease', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) } }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 34, height: 34 }}>
                    {React.cloneElement(a.icon, { sx: { fontSize: 18 } })}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{a.label}</Typography>
                  <ArrowForward sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* Recent activity */}
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Playfair Display", serif' }}>Recent activity</Typography>
            {recentActivity.length > 0 ? (
              <Stack spacing={2}>
                {recentActivity.map((a) => (
                  <Box key={a.id} sx={{ display: 'flex', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 36, height: 36 }}>
                      <BookOnline sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.description}{a.timestamp ? ` · ${new Date(a.timestamp).toLocaleDateString()}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No recent activity</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDashboard;
